import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Plus,
    Edit2,
    Trash2,
    RefreshCw,
    Search,
    CalendarDays,
    Eye,
    User,
    ShieldCheck,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
} from "lucide-react";
import baseapi from "../../services/baseapi";
import { showError, showSuccess } from "../../utils/toast";

import DashboardPreviewModal from "./DashboardPreviewModal";
import DeleteConfirmModal from "./DeleteConfirmModal";
import { useClientLevel } from "../../hooks/useClientLevel";

interface AnalyticsDashboard {
    id: number;
    name: string;
    description: string;
    client_id: number | null;
    is_global: boolean;
    is_public: boolean;
    is_owner: boolean;
    permission: string;
    created: string;
    updated: string;
}

export default function AnalyticsDashboardTable() {
    const navigate = useNavigate();
    const [dashboards, setDashboards] = useState<AnalyticsDashboard[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const level = useClientLevel();
    const clientId = level.id;

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    const totalPages = Math.ceil(totalRecords / limit);
    const startIndex = (currentPage - 1) * limit;
    const endIndex = startIndex + limit;



    // Preview Modal State
    const [previewDashboardId, setPreviewDashboardId] = useState<number | null>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    // Delete Modal State
    const [deleteDashboardId, setDeleteDashboardId] = useState<number | null>(null);
    const [deleteDashboardName, setDeleteDashboardName] = useState<string>("");
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Refresh Key
    const [refreshKey, setRefreshKey] = useState(0);

    const fetchDashboards = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;

            const params: any = {
                skip,
                limit,
            };

            if (clientId) {
                params.client_id = clientId;
            }

            if (searchTerm) {
                params.search = searchTerm;
            }

            const response = await baseapi.get("/analytics/dashboards", { params });

            if (response.data && response.data.status) {
                setDashboards(response.data.data || []);
                setTotalRecords(response.data.total || 0);
            } else {
                setDashboards([]);
                setTotalRecords(0);
            }
        } catch (error: any) {
            showError(error.message || "Failed to load dashboards");
            setDashboards([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboards();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, limit, searchTerm, clientId, refreshKey]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        setSearchTerm(searchInput.trim());
    };

    const confirmDelete = async () => {
        if (!deleteDashboardId) return;
        setIsDeleting(true);
        try {
            await baseapi.delete(`/analytics/dashboards/${deleteDashboardId}`);
            showSuccess("Dashboard deleted successfully");
            setIsDeleteModalOpen(false);
            setDeleteDashboardId(null);
            setDeleteDashboardName("");
            let nextPage = currentPage;
            if (dashboards.length === 1 && currentPage > 1) {
                nextPage = currentPage - 1;
                setCurrentPage(nextPage);
            }
            setRefreshKey((prev) => prev + 1);
        } catch (error: any) {
            showError(error.message || "Failed to delete dashboard");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteClick = (db: AnalyticsDashboard) => {
        setDeleteDashboardId(db.id);
        setDeleteDashboardName(db.name || "this dashboard");
        setIsDeleteModalOpen(true);
    };



    const formatDate = (dateStr: string) => {
        try {
            return new Date(dateStr).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
            });
        } catch {
            return dateStr;
        }
    };

    return (
        <div className="flex flex-col h-full text-gray-200 relative p-2 pb-6">
            {/* Tabs Bar */}
            <div className="flex border-b border-gray-700 mb-3 gap-2 shrink-0">
                <button
                    onClick={() => navigate("/custom-dashboard")}
                    className="px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 text-white border-orange-500 bg-gray-800/40 rounded-t-md transition-all"
                >
                    Dashboards
                </button>
                <button
                    onClick={() => navigate("/custom-dashboard/visualize")}
                    className="px-4 py-2 text-xs font-bold uppercase tracking-wider border-b-2 text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-800/20 transition-all rounded-t-md"
                >
                    Widgets
                </button>
            </div>

            {/* Header */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-3 border-b border-gray-700 pb-3">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-500/10 rounded mr-2.5">
                        <LayoutDashboard className="w-4 h-4 text-orange-400" />
                    </div>
                    <div className="flex flex-col">
                        <h2 className="text-lg font-bold text-white leading-tight">Dashboards</h2>
                        <p className="text-gray-200 text-[11px] uppercase font-semibold tracking-wider">
                            {totalRecords || "0"} dashboards found
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <form onSubmit={handleSearchSubmit} className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-200" />
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search dashboards..."
                            className="pl-8 pr-3 py-1.5 rounded-md bg-gray-800 text-xs text-gray-200 border border-gray-600 focus:outline-none focus:border-orange-500 w-56 transition-colors"
                        />
                    </form>
                    <button
                        onClick={() => {
                            setCurrentPage(1);
                            setSearchInput("");
                            setSearchTerm("");
                            setRefreshKey((prev) => prev + 1);
                        }}
                        disabled={loading}
                        className="p-1.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700 transition-all group shrink-0 disabled:opacity-50"
                    >
                        <RefreshCw
                            size={14}
                            className={loading ? "animate-spin" : "group-hover:text-orange-400"}
                        />
                    </button>
                    <button
                        onClick={() => navigate("/custom-dashboard/create")}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white text-xs font-semibold rounded-lg transition-colors border border-orange-500"
                    >
                        <Plus size={14} /> Create
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-gray-900/40 rounded-md border border-gray-700 overflow-hidden flex flex-col relative">
                <div className="flex-1 overflow-x-auto overflow-y-auto custom-scroll">
                    <table className="w-full table-fixed text-left min-w-[700px]">
                        <colgroup>
                            <col className="w-[28%]" />
                            <col className="w-[34%]" />
                            <col className="w-[14%]" />
                            <col className="w-[14%]" />
                            <col className="w-[10%]" />
                        </colgroup>
                        <thead className="bg-gray-800 sticky top-0 z-10">
                            <tr className="border-b border-gray-700">
                                <th className="px-3 py-2 text-[10px] font-bold text-gray-300 uppercase tracking-wider">
                                    Name
                                </th>
                                <th className="px-3 py-2 text-[10px] font-bold text-gray-300 uppercase tracking-wider">
                                    Description
                                </th>
                                <th className="px-3 py-2 text-[10px] font-bold text-gray-300 uppercase tracking-wider">
                                    Owner
                                </th>
                                <th className="px-3 py-2 text-[10px] font-bold text-gray-300 uppercase tracking-wider">
                                    Updated
                                </th>
                                <th className="px-3 py-2 text-[10px] font-bold text-gray-300 uppercase tracking-wider text-right">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800/50">
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="h-32">
                                        <div className="flex flex-col items-center justify-center space-y-3 h-full">
                                            <div className="w-6 h-6 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin" />
                                        </div>
                                    </td>
                                </tr>
                            ) : dashboards.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="h-32">
                                        <div className="flex flex-col items-center justify-center space-y-2 opacity-50 h-full">
                                            <LayoutDashboard
                                                size={32}
                                                className="text-gray-200 mb-2"
                                            />
                                            <p className="text-[11px] font-bold text-gray-200 uppercase tracking-widest">
                                                No dashboards found
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                dashboards.map((db) => (
                                    <tr
                                        key={db.id}
                                        className="hover:bg-gray-800/40 transition-colors duration-100"
                                    >
                                        {/* Name */}
                                        <td className="px-3 py-2">
                                            <button
                                                onClick={() =>
                                                    navigate(
                                                        `/custom-dashboard/create?id=${db.id}`
                                                    )
                                                }
                                                className="text-[12px] font-bold text-white hover:text-orange-400 truncate text-left transition-colors"
                                                title={db.name}
                                            >
                                                {db.name}
                                            </button>
                                        </td>

                                        {/* Description */}
                                        <td className="px-3 py-2">
                                            <span
                                                className="text-[11px] text-gray-300 truncate block"
                                                title={db.description}
                                            >
                                                {db.description || "—"}
                                            </span>
                                        </td>

                                        {/* Is Owner */}
                                        <td className="px-3 py-2">
                                            {db.is_owner ? (
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-orange-500/10 text-orange-400 border border-orange-500/20">
                                                    <ShieldCheck size={9} />
                                                    Owner
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-gray-600/30 text-gray-400 border border-gray-600">
                                                    <User size={9} />
                                                    Viewer
                                                </span>
                                            )}
                                        </td>

                                        {/* Updated */}
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1.5">
                                                <CalendarDays
                                                    size={10}
                                                    className="text-gray-400 shrink-0"
                                                />
                                                <span className="text-[10px] text-gray-300">
                                                    {formatDate(db.updated)}
                                                </span>
                                            </div>
                                        </td>

                                        {/* Actions */}
                                        <td className="px-3 py-2 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => {
                                                        setPreviewDashboardId(db.id);
                                                        setIsPreviewOpen(true);
                                                    }}
                                                    className="p-1 hover:bg-orange-500/20 text-orange-400 rounded transition-colors"
                                                    title="Preview Dashboard"
                                                >
                                                    <Eye size={14} />
                                                </button>
                                                {db.is_owner && (
                                                    <>
                                                        <button
                                                            onClick={() => navigate(`/custom-dashboard/create?id=${db.id}`)}
                                                            className="p-1 hover:bg-orange-500/20 text-orange-400 rounded transition-colors"
                                                            title="Edit Dashboard"
                                                        >
                                                            <Edit2 size={14} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteClick(db)}
                                                            className="p-1 hover:bg-red-500/20 text-red-400 rounded transition-colors"
                                                            title="Delete Dashboard"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="bg-gray-900/60 border-t border-gray-700 px-4 py-2 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-4">
                        <select
                            className="px-2 py-1 bg-gray-800 border border-gray-700 rounded text-white text-[10px] focus:outline-none   cursor-pointer"
                            value={limit}
                            onChange={(e) => {
                                setLimit(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                            <span className="text-gray-200">{totalRecords === 0 ? 0 : startIndex + 1}</span>
                            {" - "}
                            <span className="text-gray-200">{Math.min(endIndex, totalRecords)}</span>
                            {" OF "}
                            <span className="text-gray-200">{totalRecords}</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                            className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400 transition-colors cursor-pointer"
                            title="First Page"
                        >
                            <ChevronsLeft className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400 transition-colors cursor-pointer"
                            title="Previous Page"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-950/20">
                            {currentPage}
                        </div>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400 transition-colors cursor-pointer"
                            title="Next Page"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 disabled:hover:text-gray-400 transition-colors cursor-pointer"
                            title="Last Page"
                        >
                            <ChevronsRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>



            {/* Dashboard Preview (full-screen) */}
            <DashboardPreviewModal
                isOpen={isPreviewOpen}
                onClose={() => { setIsPreviewOpen(false); setPreviewDashboardId(null); }}
                dashboardId={previewDashboardId}
                clientId={clientId}
            />

            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setDeleteDashboardId(null);
                    setDeleteDashboardName("");
                }}
                onConfirm={confirmDelete}
                itemName={deleteDashboardName}
                itemType="dashboard"
                busy={isDeleting}
            />
        </div>
    );
}
